import { Condition, Patient, CodeableConcept } from '@medplum/fhirtypes';
import { TFunction } from 'i18next';
import { IconSearch } from '@tabler/icons-react';
import { HL7, ARKON, SYSTEMS } from '@consts/systems.consts';
import { getPatientOrPractitionerName } from '../../lims/utils/common-helpers';
import {
  SearchFieldDefinition,
  ResourceSearchConfig,
  FhirSearchParams,
} from '@/components/resource-search/ResourceSearchForm.types';
import { ResourceTableColumn } from '@/components/resource-search/ResourceTable';
import { BaseSearchParams } from '../../lims/hooks/useResourceSearch';
import { StatusBadge } from '@/components/medplum-react/StatusBadge/StatusBadge';

export interface ConditionSearchParams extends BaseSearchParams {
  patientName?: string;
  code?: string;
  codeDisplay?: string;
  category?: string;
  clinicalStatus?: string;
}

export const CONDITION_SEARCH_DEFAULTS: ConditionSearchParams = {
  patientName: undefined,
  code: undefined,
  codeDisplay: undefined,
  category: undefined,
  clinicalStatus: undefined,
  dateFrom: null,
  dateTo: null,
};

export const conditionSearchConfig: ResourceSearchConfig<
  ConditionSearchParams,
  Condition
> = {
  resourceType: 'Condition',
  itemsPerPage: 20,
  buildSearchQuery: (params, page, itemsPerPage) => {
    const query: FhirSearchParams = {
      _count: itemsPerPage,
      _offset: (page - 1) * itemsPerPage,
      _sort: '-onset-date',
      'code:not': `${SYSTEMS.HPO}|`,
      'category:not': `${HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.BASE}|${HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.CODES.PROBLEM_LIST_ITEM}`,
    };

    if (params.code) {
      query.code = params.code;
    }

    if (params.category) {
      query.category = params.category;
    }

    if (params.clinicalStatus) {
      query['clinical-status'] = params.clinicalStatus;
    }

    if (params.patientName) {
      query['subject:Patient.name'] = params.patientName;
    }

    if (params.dateFrom && params.dateTo) {
      const from = params.dateFrom.toISOString().split('T')[0];
      const to = params.dateTo.toISOString().split('T')[0];
      query._filter = `(onset-date ge ${from} and onset-date le ${to})`;
    } else if (params.dateFrom) {
      query['onset-date'] = `ge${params.dateFrom.toISOString().split('T')[0]}`;
    } else if (params.dateTo) {
      query['onset-date'] = `le${params.dateTo.toISOString().split('T')[0]}`;
    }

    return query;
  },
};

const CONDITION_CATEGORY_OPTIONS = [
  {
    value: `${HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.BASE}|medical-history`,
    translationKey: 'medicalHistory',
    defaultLabel: 'Medical history',
  },

  {
    value: `${HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.BASE}|encounter-diagnosis`,
    translationKey: 'encounterDiagnosis',
    defaultLabel: 'Encounter diagnosis',
  },
] as const;

const CONDITION_CLINICAL_STATUS_OPTIONS = [
  {
    value: `${HL7.TERMINOLOGY.CS.CONDITION.CLINICAL.BASE}|active`,
    translationKey: 'active',
    defaultLabel: 'Active',
  },
  {
    value: `${HL7.TERMINOLOGY.CS.CONDITION.CLINICAL.BASE}|inactive`,
    translationKey: 'inactive',
    defaultLabel: 'Inactive',
  },
  {
    value: `${HL7.TERMINOLOGY.CS.CONDITION.CLINICAL.BASE}|resolved`,
    translationKey: 'resolved',
    defaultLabel: 'Resolved',
  },
] as const;

interface ConditionFieldOptions {
  t: TFunction<'patient-pages' | 'lims'>;
  searchParams: ConditionSearchParams;
  onSearch: (updates: Partial<ConditionSearchParams>) => void;
  hidePatientName?: boolean;
  patientNameQuery: string;
  setPatientNameQuery: (value: string) => void;
  setPatientQuery: (value: string) => void;
  patientNameOptions: string[];
  patientLoading: boolean;
  patientNameLoading: boolean;
}

export function getConditionSearchFields({
  t,
  searchParams,
  onSearch,
  hidePatientName,
  patientNameQuery,
  setPatientNameQuery,
  setPatientQuery,
  patientNameOptions,
  patientLoading,
  patientNameLoading,
}: ConditionFieldOptions): SearchFieldDefinition<ConditionSearchParams>[] {
  return [
    !hidePatientName && {
      key: 'patientName',
      type: 'autocomplete',
      placeholder: t('diagnosis.filters.patientName', {
        defaultValue: 'Search by patient name...',
      }),
      icon: <IconSearch size={16} />,
      value: patientNameQuery,
      options: patientNameOptions,
      loading: patientLoading || patientNameLoading,
      onQueryChange: (value: string) => {
        setPatientQuery(value);
        setPatientNameQuery(value);
        if (!value) {
          onSearch({ patientName: undefined });
        }
      },
      onOptionSubmit: (value: string) => onSearch({ patientName: value }),
      onEnter: (value: string) => onSearch({ patientName: value }),
    },
    {
      key: 'code',
      type: 'codeableConcept',
      placeholder: t('diagnosis.filters.code', {
        defaultValue: 'Select diagnosis code...',
      }),
      binding: ARKON.VS.ICD_10_EL,
      defaultValue: searchParams.code
        ? {
            coding: [
              {
                code: searchParams.code,
                display: searchParams.codeDisplay,
              },
            ],
          }
        : undefined,
      onChange: (value: CodeableConcept | undefined) => {
        const code = value?.coding?.[0]?.code;
        const display = value?.coding?.[0]?.display ?? value?.text;
        onSearch({
          code,
          codeDisplay: display,
        });
      },
    },
    {
      key: 'category',
      type: 'select',
      placeholder: t('diagnosis.filters.categoryPlaceholder', {
        defaultValue: 'Select category...',
      }),
      options: CONDITION_CATEGORY_OPTIONS.map((option) => ({
        value: option.value,
        label: t(`diagnosis.category.${option.translationKey}`, {
          defaultValue: option.defaultLabel,
        }),
      })),
      value: searchParams.category,
      onChange: (value: string | null) =>
        onSearch({ category: value || undefined }),
    },
    {
      key: 'clinicalStatus',
      type: 'select',
      placeholder: t('diagnosis.filters.statusPlaceholder', {
        defaultValue: 'Select clinical status...',
      }),
      options: CONDITION_CLINICAL_STATUS_OPTIONS.map((option) => ({
        value: option.value,
        label: t(`diagnosis.clinicalStatus.${option.translationKey}`, {
          defaultValue: option.defaultLabel,
        }),
      })),
      value: searchParams.clinicalStatus,
      onChange: (value: string | null) =>
        onSearch({ clinicalStatus: value || undefined }),
    },
    {
      key: 'dateFrom',
      type: 'date',
      placeholder: t('diagnosis.filters.fromDate', {
        defaultValue: 'From date',
      }),
      value: searchParams.dateFrom,
      onChange: (value: Date | null) => onSearch({ dateFrom: value }),
    },
    {
      key: 'dateTo',
      type: 'date',
      placeholder: t('diagnosis.filters.toDate', {
        defaultValue: 'To date',
      }),
      value: searchParams.dateTo,
      onChange: (value: Date | null) => onSearch({ dateTo: value }),
    },
  ].filter(Boolean) as SearchFieldDefinition<ConditionSearchParams>[];
}

interface ConditionColumnOptions {
  t: TFunction<'patient-pages' | 'lims'>;
  patients: Map<string, Patient>;
  hidePatientColumn?: boolean;
}

export function getConditionTableColumns({
  t,
  patients,
  hidePatientColumn,
}: ConditionColumnOptions): ResourceTableColumn<Condition>[] {
  const columns: ResourceTableColumn<Condition>[] = [
    {
      key: 'code',
      label: t('diagnosis.table.code', { defaultValue: 'Diagnosis' }),
      render: (condition) =>
        condition.code?.text ??
        condition.code?.coding?.[0]?.display ??
        condition.code?.coding?.[0]?.code ??
        '—',
    },
  ];

  if (!hidePatientColumn) {
    columns.push({
      key: 'patient',
      label: t('diagnosis.table.patient', { defaultValue: 'Patient' }),
      render: (condition) => {
        const patientId = condition.subject?.reference?.replace('Patient/', '');
        if (!patientId) {
          return '—';
        }
        const patient = patients.get(patientId);
        return patient
          ? getPatientOrPractitionerName(patient)
          : t('diagnosis.table.unknownPatient', { defaultValue: 'Unknown' });
      },
    });
  }

  columns.push(
    {
      key: 'clinicalStatus',
      label: t('diagnosis.table.status', { defaultValue: 'Status' }),
      render: (condition) => {
        const coding = condition.clinicalStatus?.coding?.[0];
        if (!coding?.code) {
          return '—';
        }
        const statusLabel = t(`diagnosis.clinicalStatus.${coding.code}`, {
          defaultValue: coding.display ?? coding.code,
        });
        return (
          <StatusBadge status={coding.code} label={statusLabel} size="sm" />
        );
      },
    },
    {
      key: 'category',
      label: t('diagnosis.table.category', { defaultValue: 'Category' }),
      render: (condition) => {
        const coding = condition.category?.[0]?.coding?.[0];
        if (!coding?.code) {
          return '—';
        }
        return t(`diagnosis.category.${coding.code}`, {
          defaultValue: coding.display ?? coding.code,
        });
      },
    },
    {
      key: 'onsetDate',
      label: t('diagnosis.table.onsetDate', {
        defaultValue: 'Onset date',
      }),
      render: (condition) =>
        condition.onsetDateTime
          ? new Date(condition.onsetDateTime).toLocaleDateString('en-GB')
          : condition.recordedDate
            ? new Date(condition.recordedDate).toLocaleDateString('en-GB')
            : '—',
    }
  );

  return columns;
}
